import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  readSiteSettingsFromStorage,
  SITE_BRANDING_UPDATED_EVENT,
  getPromoPopupImageUrl,
  clearPromoPopupShownFlags,
} from '../utils/siteBranding';
import {
  extractCreatorIdFromPath,
  getPromoPageTypeFromPath,
  isGlobalPromoAllowedOnPage,
  isPromoConfigActive,
  mergeCreatorPromoSources,
  promoStorageFingerprintKey,
  readCreatorPromoFromStorage,
  STOREFRONT_SEGMENT_MODE_EVENT,
} from '../utils/promoPopup';
import { getSingleSegmentFromPath } from '../utils/creatorSlug';
import { creatorStorefrontService } from '../services/api';
import './PromoPopup.css';
import { isAdminPanelPath } from '../constants/adminPanelRoutes';
import { loadPrefs } from '../context/PushNotificationContext';
import { appendNotificationHistory } from '../utils/notificationHistory';
import {
  incrementUnreadNotificationCount,
  isCoarsePointerMobile,
  syncNotificationIndicators,
  vibrateNotificationPulse,
} from '../utils/notificationIndicators';

/** Old code used this single key — blocked one tab forever */
const LEGACY_DISPLAYED_KEY = 'sheenwear_promo_popup_displayed_session';

/** WebView fallback when sessionStorage is unusable */
const LS_FALLBACK_MS = 4 * 60 * 60 * 1000;

function localGetJson(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function localSetJson(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

function sessionStorageWorks() {
  try {
    const k = '__sheen_promo_t__';
    sessionStorage.setItem(k, '1');
    sessionStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function isAlreadyShownForPromo(fpKey) {
  if (sessionStorageWorks()) {
    try {
      return sessionStorage.getItem(fpKey) === '1';
    } catch {
      /* fall through to local */
    }
  }
  const ls = localGetJson(fpKey);
  if (ls && typeof ls.at === 'number') {
    if (Date.now() - ls.at > LS_FALLBACK_MS) {
      try {
        localStorage.removeItem(fpKey);
      } catch {
        /* ignore */
      }
      return false;
    }
    return true;
  }
  return false;
}

function markPromoShown(fpKey) {
  if (sessionStorageWorks()) {
    try {
      sessionStorage.setItem(fpKey, '1');
      try {
        localStorage.removeItem(fpKey);
      } catch {
        /* ignore */
      }
      return;
    } catch {
      /* fall through */
    }
  }
  localSetJson(fpKey, { at: Date.now() });
}

function clamp(n, min, max) {
  const x = Number(n);
  if (Number.isNaN(x)) return min;
  return Math.max(min, Math.min(max, x));
}

const PROMO_AUTOCLOSE_FALLBACK_SEC = 5;

export default function PromoPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [imgBroken, setImgBroken] = useState(false);
  const [creatorPromo, setCreatorPromo] = useState(null);
  const [creatorPromoLoaded, setCreatorPromoLoaded] = useState(false);
  const [creatorSessionId, setCreatorSessionId] = useState('');
  const [segmentMode, setSegmentMode] = useState(null);

  useEffect(() => {
    setSegmentMode(null);
    const onMode = (event) => {
      const mode = event?.detail?.mode;
      if (mode === 'shop' || mode === 'product') setSegmentMode(mode);
    };
    window.addEventListener(STOREFRONT_SEGMENT_MODE_EVENT, onMode);
    return () => window.removeEventListener(STOREFRONT_SEGMENT_MODE_EVENT, onMode);
  }, [location.pathname]);

  const pageType = useMemo(() => {
    const base = getPromoPageTypeFromPath(location.pathname);
    if (base) return base;
    if (segmentMode === 'shop') return 'creator';
    if (segmentMode === 'product') return 'product';
    return null;
  }, [location.pathname, segmentMode]);

  const creatorId = useMemo(() => {
    if (pageType !== 'creator') return '';
    return (
      extractCreatorIdFromPath(location.pathname) ||
      getSingleSegmentFromPath(location.pathname)
    );
  }, [pageType, location.pathname]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    window.addEventListener(SITE_BRANDING_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(SITE_BRANDING_UPDATED_EVENT, refresh);
  }, [refresh]);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const v = (sp.get('resetPromo') || '').toLowerCase();
      if (v === '1' || v === 'true' || v === 'yes') {
        clearPromoPopupShownFlags();
        refresh();
        sp.delete('resetPromo');
        const qs = sp.toString();
        const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
        window.history.replaceState(null, '', next);
      }
    } catch {
      /* ignore */
    }
  }, [refresh]);

  useEffect(() => {
    try {
      sessionStorage.removeItem(LEGACY_DISPLAYED_KEY);
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem(LEGACY_DISPLAYED_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setOpen(false);
    setImgBroken(false);
    setCreatorPromo(null);
    setCreatorPromoLoaded(false);
  }, [location.pathname]);

  useEffect(() => {
    if (pageType !== 'creator' || !creatorId) {
      setCreatorSessionId('');
      setCreatorPromoLoaded(pageType !== null);
      return undefined;
    }

    let cancelled = false;
    setCreatorPromoLoaded(false);

    const run = async () => {
      let apiProfile = null;
      try {
        const res = await creatorStorefrontService.getCreatorProfile(creatorId);
        apiProfile = res?.creator;
      } catch {
        /* ignore */
      }
      const canonicalId = apiProfile?._id || apiProfile?.id || creatorId;
      const stored = readCreatorPromoFromStorage(canonicalId);
      if (cancelled) return;
      setCreatorSessionId(String(canonicalId));
      setCreatorPromo(mergeCreatorPromoSources(stored, apiProfile));
      setCreatorPromoLoaded(true);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [pageType, creatorId, tick]);

  const activePromo = useMemo(() => {
    const site = readSiteSettingsFromStorage() || {};

    if (pageType === 'creator' && creatorId) {
      if (creatorPromo && isPromoConfigActive(creatorPromo)) {
        return {
          config: creatorPromo,
          scope: `creator_${creatorSessionId || creatorId}`,
        };
      }
      if (isGlobalPromoAllowedOnPage(site, 'creator') && isPromoConfigActive(site)) {
        return { config: site, scope: 'site' };
      }
      return null;
    }

    if (!pageType) return null;
    if (!isGlobalPromoAllowedOnPage(site, pageType)) return null;
    if (!isPromoConfigActive(site)) return null;
    return { config: site, scope: 'site' };
  }, [pageType, creatorId, creatorSessionId, creatorPromo, tick]);

  useEffect(() => {
    if (isAdminPanelPath(location.pathname)) return undefined;
    if (pageType === null) return undefined;
    if (pageType === 'creator' && !creatorPromoLoaded) return undefined;
    if (!activePromo) return undefined;

    const { config, scope } = activePromo;
    const url = getPromoPopupImageUrl(config);
    if (!url) return undefined;

    const fpKey = promoStorageFingerprintKey(config, scope);
    if (isAlreadyShownForPromo(fpKey)) return undefined;

    const delaySec = clamp(config.promoPopupDelaySeconds, 0, 600);
    const delayMs = delaySec * 1000;

    const t = window.setTimeout(() => {
      markPromoShown(fpKey);
      setOpen(true);
    }, delayMs);

    return () => window.clearTimeout(t);
  }, [location.pathname, tick, pageType, creatorPromoLoaded, activePromo]);

  useEffect(() => {
    if (!open || !activePromo) return undefined;
    const configured = clamp(activePromo.config.promoPopupAutoCloseSeconds, 0, 300);
    const autoCloseSec = configured > 0 ? configured : PROMO_AUTOCLOSE_FALLBACK_SEC;
    const timer = window.setTimeout(() => setOpen(false), autoCloseSec * 1000);
    return () => window.clearTimeout(timer);
  }, [open, activePromo]);

  useEffect(() => {
    if (!open) return;
    const prefs = loadPrefs();
    if (!prefs.emailPromotional) return;
    if (isCoarsePointerMobile()) {
      vibrateNotificationPulse();
    }
    const stored = appendNotificationHistory({
      title: 'Promotion',
      message:
        'A new promotion is live. Tap to open notification settings or continue shopping.',
      path: '/profile',
      profileSection: 'notifications',
      kind: 'promo',
    });
    if (stored) {
      const total = incrementUnreadNotificationCount();
      syncNotificationIndicators(total);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const dismiss = () => setOpen(false);

  if (!open || !activePromo) return null;

  const url = getPromoPopupImageUrl(activePromo.config);
  if (!url) return null;

  const configuredClose = clamp(activePromo.config.promoPopupAutoCloseSeconds, 0, 300);
  const effectiveAutoCloseSec =
    configuredClose > 0 ? configuredClose : PROMO_AUTOCLOSE_FALLBACK_SEC;
  const autoCloseMs = effectiveAutoCloseSec * 1000;

  const node = (
    <div
      className="promo-popup-overlay"
      role="presentation"
      onClick={dismiss}
      aria-hidden={!open}
    >
      <div
        className="promo-popup-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Promotion"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="promo-popup-close"
          onClick={dismiss}
          aria-label="Close"
        >
          <i className="fas fa-times" />
        </button>
        <div className="promo-popup-image-wrap">
          {imgBroken ? (
            <p className="promo-popup-fallback">Image could not be loaded.</p>
          ) : (
            <img
              src={url}
              alt=""
              className="promo-popup-image"
              decoding="async"
              loading="eager"
              onError={() => setImgBroken(true)}
            />
          )}
        </div>
        <div className="promo-popup-progress" aria-hidden>
          <span
            className="promo-popup-progress-bar"
            style={{ animationDuration: `${autoCloseMs}ms` }}
          />
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(node, document.body);
  }
  return node;
}
